import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { AppLanguage, isRtl } from "./config";

// ─────────────────────────────────────────────
//  LanguageProvider
//
//  Single source of truth for the active language. It:
//    • keeps <html dir> / <html lang> in sync (RTL for Arabic)
//    • provides an MUI theme with the right `direction`
//    • swaps in an RTL-aware emotion cache so MUI components
//      flip automatically (margins, paddings, positions…)
//
//  Consume it anywhere with:  const { language, setLanguage } = useLanguage();
// ─────────────────────────────────────────────

interface LanguageContextValue {
  language: AppLanguage;
  isRtl: boolean;
  setLanguage: (lng: AppLanguage) => void;
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export const useLanguage = (): LanguageContextValue => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within a LanguageProvider");
  return ctx;
};

// Emotion caches — one LTR, one RTL. Created once at module load.
// `prepend: true` mirrors MUI's default cache so MUI styles stay at the top
// of <head> and the template's own CSS keeps overriding them (otherwise the
// layout breaks, as MUI styles would win the cascade).
const ltrCache = createCache({ key: "mui", prepend: true, stylisPlugins: [prefixer] });
const rtlCache = createCache({ key: "muirtl", prepend: true, stylisPlugins: [prefixer, rtlPlugin] });

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const [language, setLanguageState] = useState<AppLanguage>(
    (i18n.language?.split("-")[0] as AppLanguage) || "en"
  );

  const rtl = isRtl(language);

  // Reflect the language on <html> so both MUI and the site's own
  // Bootstrap/SCSS respond to [dir] / :lang() selectors.
  useEffect(() => {
    document.documentElement.setAttribute("lang", language);
    document.documentElement.setAttribute("dir", rtl ? "rtl" : "ltr");
  }, [language, rtl]);

  const setLanguage = useCallback(
    (lng: AppLanguage) => {
      i18n.changeLanguage(lng);
      setLanguageState(lng);
    },
    [i18n]
  );

  const toggleLanguage = useCallback(() => {
    setLanguage(language === "ar" ? "en" : "ar");
  }, [language, setLanguage]);

  // Keep local state aligned if the language changes elsewhere.
  useEffect(() => {
    const handler = (lng: string) => setLanguageState((lng.split("-")[0] as AppLanguage) || "en");
    i18n.on("languageChanged", handler);
    return () => i18n.off("languageChanged", handler);
  }, [i18n]);

  const theme = useMemo(() => createTheme({ direction: rtl ? "rtl" : "ltr" }), [rtl]);

  const value = useMemo<LanguageContextValue>(
    () => ({ language, isRtl: rtl, setLanguage, toggleLanguage }),
    [language, rtl, setLanguage, toggleLanguage]
  );

  return (
    <LanguageContext.Provider value={value}>
      <CacheProvider value={rtl ? rtlCache : ltrCache}>
        <ThemeProvider theme={theme}>{children}</ThemeProvider>
      </CacheProvider>
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
